import { findColumn, locateBlockById } from "../find";
import type { DragData } from "../dnd/types";
import type { EmailDocument } from "../types";
import { resolveColumnSiblingList } from "./block-selection-gesture";
import type { SelectionCapabilities } from "./policy";

export function blockIdsInDragItem(
  drag: Extract<DragData, { kind: "item-block" }>,
): string[] {
  return drag.blockIds?.length ? drag.blockIds : [drag.blockId];
}

export function resolveMovableGroupBlockIds(
  document: EmailDocument,
  selectedBlockIds: readonly string[],
  activeBlockId: string,
): string[] {
  if (
    !selectedBlockIds.includes(activeBlockId) ||
    selectedBlockIds.length <= 1
  ) {
    return [activeBlockId];
  }

  const ordered = resolveOrderedColumnBlockIds(document, [...selectedBlockIds]);
  if (!ordered.ok) {
    return [activeBlockId];
  }

  const columnBlockIds = columnBlockIdsForDocument(
    document,
    ordered.rowId,
    ordered.columnId,
  );
  if (
    !ordered.blockIds.includes(activeBlockId) ||
    !isContiguousColumnBlockSelection(ordered.blockIds, columnBlockIds)
  ) {
    return [activeBlockId];
  }

  return ordered.blockIds;
}

export type OrderedColumnBlockIds =
  | {
      ok: true;
      rowId: string;
      columnId: string;
      blockIds: string[];
    }
  | {
      ok: false;
    };

function orderIdsInColumn(blockIds: string[], columnBlockIds: string[]): string[] {
  const selected = new Set(blockIds);
  return columnBlockIds.filter(id => selected.has(id));
}

export function resolveOrderedColumnBlockIds(
  document: EmailDocument,
  blockIds: string[],
): OrderedColumnBlockIds {
  if (blockIds.length === 0) {
    return { ok: false };
  }

  const first = locateBlockById(document, blockIds[0]);
  if (!first) {
    return { ok: false };
  }

  const firstList = resolveColumnSiblingList(document, blockIds[0]);
  if (!firstList) {
    return { ok: false };
  }

  for (const blockId of blockIds) {
    const located = locateBlockById(document, blockId);
    if (
      !located ||
      located.rowId !== firstList.rowId ||
      located.columnId !== firstList.columnId
    ) {
      return { ok: false };
    }
  }

  const ordered = orderIdsInColumn(blockIds, firstList.blockIds);
  if (ordered.length !== blockIds.length) {
    return { ok: false };
  }

  return {
    ok: true,
    rowId: firstList.rowId,
    columnId: firstList.columnId,
    blockIds: ordered,
  };
}

export function isContiguousColumnBlockSelection(
  blockIds: string[],
  columnBlockIds: string[],
): boolean {
  if (blockIds.length <= 1) {
    return true;
  }

  const ordered = orderIdsInColumn(blockIds, columnBlockIds);
  if (ordered.length !== blockIds.length) {
    return false;
  }

  const indices = ordered.map(id => columnBlockIds.indexOf(id));
  if (indices.some(index => index < 0)) {
    return false;
  }

  const min = Math.min(...indices);
  const max = Math.max(...indices);
  return max - min + 1 === ordered.length;
}

export function columnBlockIdsForDocument(
  document: EmailDocument,
  rowId: string,
  columnId: string,
): string[] {
  const column = findColumn(document, rowId, columnId);
  return column?.blocks.map(block => block.id) ?? [];
}

export function getMultiSelectBlockCapabilities(
  document: EmailDocument,
  selectedBlockIds: readonly string[],
): SelectionCapabilities | null {
  if (selectedBlockIds.length <= 1) {
    return null;
  }

  const ordered = resolveOrderedColumnBlockIds(document, [...selectedBlockIds]);
  if (!ordered.ok) {
    return null;
  }

  const columnBlockIds = columnBlockIdsForDocument(
    document,
    ordered.rowId,
    ordered.columnId,
  );
  const contiguous = isContiguousColumnBlockSelection(
    ordered.blockIds,
    columnBlockIds,
  );

  if (!contiguous) {
    return {
      canUp: false,
      canDown: false,
      canDuplicate: true,
      canDelete: true,
      canUngroup: false,
      showMoveButtons: false,
      upLabel: "Move up",
      downLabel: "Move down",
    };
  }

  const indices = ordered.blockIds.map(blockId => columnBlockIds.indexOf(blockId));
  const minIndex = Math.min(...indices);
  const maxIndex = Math.max(...indices);

  return {
    canUp: minIndex > 0,
    canDown: maxIndex < columnBlockIds.length - 1,
    canDuplicate: true,
    canDelete: true,
    canUngroup: false,
    showMoveButtons: true,
    upLabel: "Move up",
    downLabel: "Move down",
  };
}
